<template>
    <div class="pb-spacing">
        <div class="pb-spacing-row">
            <label v-if="label" class="pb-spacing-label">{{ label }}</label>
            <div class="pb-spacing-fields">
                <input v-if="isLinked" type="number" min="0" class="pb-spacing-input" :value="singleValue" @input="onSingle($event)" />
                <template v-else>
                    <div class="pb-spacing-group">
                        <input type="number" min="0" class="pb-spacing-input" :value="values.left" @input="onSide('left', $event)" />
                        <span>{{ sideLabel("left") }}</span>
                    </div>
                    <div class="pb-spacing-group">
                        <input type="number" min="0" class="pb-spacing-input" :value="values.top" @input="onSide('top', $event)" />
                        <span>{{ sideLabel("top") }}</span>
                    </div>
                    <div class="pb-spacing-group">
                        <input type="number" min="0" class="pb-spacing-input" :value="values.right" @input="onSide('right', $event)" />
                        <span>{{ sideLabel("right") }}</span>
                    </div>
                    <div class="pb-spacing-group">
                        <input type="number" min="0" class="pb-spacing-input" :value="values.bottom" @input="onSide('bottom', $event)" />
                        <span>{{ sideLabel("bottom") }}</span>
                    </div>
                </template>
            </div>
            <div class="pb-link-toggle">
                <button type="button" :class="{ active: isLinked }" title="All sides together" @click="setLinked(true)"><i class="fas fa-link"></i></button>
                <button type="button" :class="{ active: !isLinked }" title="Individual sides" @click="setLinked(false)"><i class="fas fa-unlink"></i></button>
            </div>
        </div>
        <small v-if="help" class="pb-help">{{ help }}</small>
    </div>
</template>

<script>
// Heygov-style spacing control: single value + link/unlink toggle. When unlinked, the 4
// inputs (L/T/R/B) share the SAME field area width as the single input (no growth). Value
// is CSS shorthand "T R B L" with a unit (default px). Emits `change`.
export default {
    name: "SpacingControl",
    props: {
        value: { type: String, default: "0px 0px 0px 0px" },
        label: { type: String, default: "" },
        type: { type: String, default: "padding" },
        help: { type: String, default: "" },
    },
    emits: ["change"],
    data() {
        return { values: { top: 0, right: 0, bottom: 0, left: 0 }, unit: "px", isLinked: true };
    },
    computed: {
        singleValue() {
            return this.values.top;
        },
    },
    created() {
        this.parseValue(this.value);
    },
    watch: {
        value(v) {
            this.parseValue(v);
        },
    },
    methods: {
        parseValue(value) {
            if (!value || value === "") {
                this.values = { top: 0, right: 0, bottom: 0, left: 0 };
                this.unit = "px";
                this.isLinked = true;
                return;
            }
            const parts = value.trim().split(/\s+/);
            if (parts.length === 4) {
                const unitMatch = parts[0].match(/[a-z%]+/);
                this.unit = unitMatch ? unitMatch[0] : "px";
                this.values.top = parseFloat(parts[0]) || 0;
                this.values.right = parseFloat(parts[1]) || 0;
                this.values.bottom = parseFloat(parts[2]) || 0;
                this.values.left = parseFloat(parts[3]) || 0;
                this.isLinked = this.values.top === this.values.right && this.values.right === this.values.bottom && this.values.bottom === this.values.left;
            }
        },
        setLinked(v) {
            this.isLinked = v;
            if (v) {
                const first = this.values.top || this.values.right || this.values.bottom || this.values.left || 0;
                this.values = { top: first, right: first, bottom: first, left: first };
                this.emitValue();
            }
        },
        onSingle(event) {
            const n = parseFloat(event.target.value) || 0;
            this.values = { top: n, right: n, bottom: n, left: n };
            this.emitValue();
        },
        onSide(side, event) {
            this.values[side] = parseFloat(event.target.value) || 0;
            this.emitValue();
        },
        sideLabel(side) {
            if (this.type === "borderRadius") return { top: "TL", right: "TR", bottom: "BR", left: "BL" }[side];
            return side.charAt(0).toUpperCase();
        },
        emitValue() {
            const u = this.unit || "px";
            this.$emit("change", `${this.values.top}${u} ${this.values.right}${u} ${this.values.bottom}${u} ${this.values.left}${u}`);
        },
    },
};
</script>

<style scoped>
/* fixed compact width, ~like the color picker control (a touch wider) — the field area
   holds either the single input or the 4 small L/T/R/B inputs at the SAME total width */
.pb-spacing {
    width: 275px;
    max-width: 100%;
}
.pb-spacing-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.pb-spacing-label {
    font-size: 12px;
    color: #6b7171;
    font-weight: 500;
    flex-shrink: 0;
}
/* the field area is always flex:1 — same total width whether it holds 1 or 4 inputs */
.pb-spacing-fields {
    flex: 1;
    min-width: 0;
    display: flex;
    gap: 6px;
}
.pb-spacing-input {
    flex: 1;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    height: 31.5px;
    font-size: 13px;
    background-color: #fff;
    border: none;
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
    color: #333;
    outline: none;
    -moz-appearance: textfield;
}
.pb-spacing-input::-webkit-inner-spin-button,
.pb-spacing-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
.pb-spacing-input:focus {
    background-color: #eef0f2;
}
.pb-spacing-group {
    flex: 1;
    min-width: 0;
    position: relative;
}
.pb-spacing-group .pb-spacing-input {
    padding: 0 20px 0 4px;
    text-align: left;
}
.pb-spacing-group span {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    font-weight: 600;
    color: #9ca3af;
    pointer-events: none;
}
.pb-link-toggle {
    display: inline-flex;
    flex-shrink: 0;
    border-radius: 4px;
    overflow: hidden;
    background: #e7ebef;
}
.pb-link-toggle button {
    border: none;
    background: transparent;
    color: #9ca3af;
    width: 30px;
    height: 31.5px;
    cursor: pointer;
    font-size: 12px;
}
.pb-link-toggle button.active {
    background: #fff;
    color: #2563eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.pb-help {
    display: block;
    margin-top: 6px;
    font-size: 10px;
    color: #9ca3af;
}
</style>
